import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { Accordion } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { getError } from '../../languages/translations/response';
import { ContactDA } from '../../services/contact.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { Contact } from '../../models/contact.model';
import { legendValidIcon, legendValidInvalidRestart, legendValidInvalidIconRestart } from '../../tools/legend.data.entry.tool';
import { modalHide, modalShow } from '../../tools/modal.tool';
import { uploadTooltip } from '../../tools/tooltip.tool';
import { bicFormat, ibanFormat } from '../../scripts/format.value.script';
import { evaluateLegendValidEmpty, evaluateLegendValidEmptyIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { removeUploadedFile, uploadedUrl } from '../../scripts/file.load.script';

import AppLegend from '../element/Legend';
import AppModalContactUpdateExtend from './ModalContactUpdateExtend';
import AppInputImage from '../element/InputImage';
import AppLoading from '../Loading';
import AppInputCountry from '../element/InputCountry';
import AppInputCurrency from '../element/InputCurrency';
import AppInputLanguage from '../element/InputLanguage';
import AppInputQrType from '../element/InputQrType';
import iconCard from '../../assets/images/icons/card-secret-icon.svg';
import iconBank from '../../assets/images/icons/bank-ico.svg';

export interface AppModalContactUpdateProps {
  companyForUser: Company | null,
  starting: boolean,
  contact: Contact | null,
  contacts?: Contact[] | null,
  companiesOfContact: Company[] | null,
  setStarting: Dispatch<SetStateAction<boolean>>,
  setContact: Dispatch<SetStateAction<Contact | null>>,
  setCompaniesOfContact?: Dispatch<SetStateAction<Company[] | null>>,
  setContacts?: Dispatch<SetStateAction<Contact[] | null>>,
  loadContactsForCompany?: Function
};

let errorResponse: Error, contactResponse: Contact;

const AppModalContactUpdate: React.FunctionComponent<AppModalContactUpdateProps> = ({companyForUser, starting, contact, contacts, companiesOfContact, setStarting, setContact, setCompaniesOfContact, setContacts, loadContactsForCompany}) => {
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: true})
  const [firstName, setFirstName] = useState({value: '', valid: true})
  const [lastName, setLastName] = useState({value: '', valid: true})
  const [logo, setLogo] = useState({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: true, message: ''}})
  const [email, setEmail] = useState({value: '', valid: true})
  const [phone, setPhone] = useState({value: '', valid: true})
  const [website, setWebsite] = useState({value: '', valid: true})
  const [vatNumber, setVatNumber] = useState({value: '', valid: true})
  const [registerNumber, setRegisterNumber] = useState({value: '', valid: true})
  const [street, setStreet] = useState({value: '', valid: true})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: true})
  const [additional, setAdditional] = useState({value: '', valid: true})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: true})
  const [city, setCity] = useState({value: '', valid: true})
  const [zipCode, setZipCode] = useState({value: '', valid: true})
  const [country, setCountry] = useState({value: '', valid: true})
  const [currency, setCurrency] = useState({value: '', valid: true, object: null as any})
  const [financialEntity, setFinancialEntity] = useState({value: '', valid: true})
  const [iban, setIban] = useState({value: '', valid: true})
  const [bban, setBban] = useState({value: '', valid: true})
  const [bic, setBic] = useState({value: '', valid: true})
  const [language, setLanguage] = useState({value: '', valid: true})
  const [qrType, setQrType] = useState({value: '', valid: true})
  const [values, setValues] = useState({name: '', first_name: '', last_name: '', logo: null, email: '', phone: '', website: '', register_number: '', vat_number: '', street: '', street_number: '', additional: '', administrative_area: '', city: '', zip_code: '', country: '', currency: {code: '', name: '', symbol: ''} , financial_entity: '', iban: '', bban: '', bic: '', language: '', qr_type: ''})

  const submitContactUpdate = async () => {
    setLoadIndicator('on')

    if (companyForUser && contact && name.valid && firstName.valid && lastName.valid && logo.valid && email.valid && phone.valid && website.valid && vatNumber.valid && registerNumber.valid && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid && currency.valid && financialEntity.valid && iban.valid && bban.valid && bic.valid && language.valid && qrType.valid) {
      let addressStreetNumber = (streetNumber.value === 'NN' || streetNumber.value === 'nn') ? streetNumber.value.toUpperCase() : streetNumber.value
      let valueIban = iban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBban = bban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBic = bic.value.toUpperCase().trim().replaceAll(' ', '')

      await ContactDA.putContact(companyForUser.id, contact.data.id, contact.type, name.value, firstName.value, lastName.value, logo.value.file, email.value, phone.value, website.value, vatNumber.value, registerNumber.value, street.value, addressStreetNumber, additional.value, administrativeArea.value, city.value, zipCode.value, country.value, currency.object, financialEntity.value, valueIban, valueBban, valueBic, language.value, qrType.value, []).then( (response) => {
        if (response.status === 200) {
          contactResponse = response.data

          if (contacts && setContacts && setCompaniesOfContact && loadContactsForCompany) {
            let tempContacts = contacts.map((temp_item) => {
              if (temp_item.data.id === contact.data.id) {
                temp_item = contactResponse
              }
              return temp_item
            })
            setContacts(tempContacts)
            loadContactsForCompany(companyForUser.id)
          } else {
            setContact(contactResponse)
          }

          Swal.fire({
            title: lang.labels.successfullyUpdatedContact,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 1800
          } as SweetAlertOptions).then( () => {
            setLoadIndicator('off')
            executeHideModalContactUpdate()
          })
        } else {
          errorResponse = response.data

          Swal.fire({
            title: getError(errorResponse.code, lang.code),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            buttonsStyling: !1,
            confirmButtonText: lang.labels.okGotIt,
            customClass: {confirmButton: 'btn btn-primary'}
          } as SweetAlertOptions).then( () => {
            setLoadIndicator('off')
          })
        }
      }).catch( (error) => {
        console.error(error)
        navigate('/error')
      })
    } else {
      Swal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 1800
      } as SweetAlertOptions).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeFirstName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFirstName({...firstName, value: event.target.value})
  }

  const handleChangeLastName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setLastName({...lastName, value: event.target.value})
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const handleChangePhone = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPhone({...phone, value: event.target.value})
  }

  const handleChangeWebsite = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setWebsite({...website, value: event.target.value})
  }

  const handleChangeRegisterNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setRegisterNumber({...registerNumber, value: event.target.value})
  }

  const handleChangeVatNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVatNumber({...vatNumber, value: event.target.value})
  }

  const handleChangeStreet = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreet({...street, value: event.target.value})
  }

  const handleChangeStreetNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({...streetNumber, value: event.target.value})
  }

  const handleChangeAdditional = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdditional({...additional, value: event.target.value})
  }

  const handleChangeAdministrativeArea = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdministrativeArea({...administrativeArea, value: event.target.value})
  }

  const handleChangeCity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCity({...city, value: event.target.value})
  }

  const handleChangeZipCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setZipCode({...zipCode, value: event.target.value})
  }

  const handleChangeAccountName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFinancialEntity({...financialEntity, value: event.target.value})
  }

  const handleChangeIban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setIban({...iban, value: event.target.value})
  }

  const handleChangeBban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBban({...bban, value: event.target.value})
  }

  const handleChangeBic = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBic({...bic, value: event.target.value})
  }

  const validateName = () => {
    evaluateLegendValidEmptyIcon(expressions.name, name, setName, 'modal-contact-update-input-name', 'modal-contact-update-legend-name-valid')
  }

  const validateFirstName = () => {
    evaluateLegendValidEmptyIcon(expressions.name, firstName, setFirstName, 'modal-contact-update-input-first-name', 'modal-contact-update-legend-first-name-valid')
  }

  const validateLastName = () => {
    evaluateLegendValidEmptyIcon(expressions.name, lastName, setLastName, 'modal-contact-update-input-last-name', 'modal-contact-update-legend-last-name-valid')
  }

  const validateEmail = () => {
    evaluateLegendValidEmptyIcon(expressions.email, email, setEmail, 'modal-contact-update-input-email', 'modal-contact-update-legend-email-valid')
  }

  const validatePhone = () => {
    evaluateLegendValidEmptyIcon(expressions.phone, phone, setPhone, 'modal-contact-update-input-phone', 'modal-contact-update-legend-phone-valid')
  }

  const validateWebsite = () => {
    evaluateLegendValidEmptyIcon(expressions.website, website, setWebsite, 'modal-contact-update-input-website', 'modal-contact-update-legend-website-valid')
  }

  const validateRegisterNumber = () => {
    evaluateLegendValidEmptyIcon(expressions.registernumber, registerNumber, setRegisterNumber, 'modal-contact-update-input-register-number', 'modal-contact-update-legend-register-number-valid')
  }

  const validateVatNumber = () => {
    evaluateLegendValidEmptyIcon(expressions.vat, vatNumber, setVatNumber, 'modal-contact-update-input-vat-number', 'modal-contact-update-legend-vat-number-valid')
  }

  const validateStreet = () => {
    evaluateLegendValidEmptyIcon(expressions.street, street, setStreet, 'modal-contact-update-input-street', 'modal-contact-update-legend-street-valid')
  }

  const validateStreetNumber = () => {
    evaluateLegendValidEmptyIcon(expressions.streetnumber, streetNumber, setStreetNumber, 'modal-contact-update-input-street-number', 'modal-contact-update-legend-street-number-valid')
  }

  const validateAdditional = () => {
    evaluateLegendValidEmptyIcon(expressions.text, additional, setAdditional, 'modal-contact-update-input-additional', 'modal-contact-update-legend-additional-valid')
  }

  const validateAdministrativeArea = () => {
    evaluateLegendValidEmptyIcon(expressions.location, administrativeArea, setAdministrativeArea, 'modal-contact-update-input-administrative-area', 'modal-contact-update-legend-administrative-area-valid')
  }

  const validateCity = () => {
    evaluateLegendValidEmptyIcon(expressions.location, city, setCity, 'modal-contact-update-input-city', 'modal-contact-update-legend-city-valid')
  }

  const validateZipCode = () => {
    evaluateLegendValidEmptyIcon(expressions.zipcode, zipCode, setZipCode, 'modal-contact-update-input-zip-code', 'modal-contact-update-legend-zip-code-valid')
  }

  const validateFinancialEntity = () => {
    evaluateLegendValidEmpty(expressions.name, financialEntity, setFinancialEntity, 'modal-contact-update-legend-financial-entity-valid')
  }

  const validateIban = () => {
    evaluateLegendValidEmpty(expressions.iban, iban, setIban, 'modal-contact-update-legend-iban-valid')
  }

  const validateBban = () => {
    evaluateLegendValidEmpty(expressions.bban, bban, setBban, 'modal-contact-update-legend-bban-valid')
  }

  const validateBic = () => {
    evaluateLegendValidEmpty(expressions.bic, bic, setBic, 'modal-contact-update-legend-bic-valid')
  }

  const executeShowModalContactUpdateExtend = () => {
    if (companyForUser && contact && name.valid && firstName.valid && lastName.valid && logo.valid.file && email.valid && phone.valid && website.valid && vatNumber.valid && registerNumber.valid && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid && currency.valid && financialEntity.valid && iban.valid && bban.valid && bic.valid && language.valid && qrType.valid) {
      setValues({name: name.value, first_name: firstName.value, last_name: lastName.value, logo: logo.value.file, email: email.value, phone: phone.value, website: website.value, register_number: registerNumber.value, vat_number: vatNumber.value, street: street.value, street_number: streetNumber.value, additional: additional.value, administrative_area: administrativeArea.value, city: city.value, zip_code: zipCode.value, country: country.value, currency: currency.object, financial_entity: financialEntity.value, iban: iban.value, bban: bban.value, bic: bic.value, language: language.value, qr_type: qrType.value})

      modalHide('modal-contact-update')
      modalShow('modal-contact-update-extend')
    } else {
      Swal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        buttonsStyling: !1,
        confirmButtonText: lang.labels.okGotIt,
        customClass: {confirmButton: 'btn btn-primary'}
      } as SweetAlertOptions).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalContactUpdate = () => {
    modalHide('modal-contact-update')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidIconRestart('modal-contact-update-input-name', 'modal-contact-update-legend-name-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-first-name', 'modal-contact-update-legend-first-name-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-last-name', 'modal-contact-update-legend-last-name-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-email', 'modal-contact-update-legend-email-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-phone', 'modal-contact-update-legend-phone-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-website', 'modal-contact-update-legend-website-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-register-number', 'modal-contact-update-legend-register-number-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-vat-number', 'modal-contact-update-legend-vat-number-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-street', 'modal-contact-update-legend-street-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-street-number', 'modal-contact-update-legend-street-number-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-additional', 'modal-contact-update-legend-additional-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-administrative-area', 'modal-contact-update-legend-administrative-area-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-city', 'modal-contact-update-legend-city-valid')
    legendValidInvalidIconRestart('modal-contact-update-input-zip-code', 'modal-contact-update-legend-zip-code-valid')
    legendValidInvalidRestart('modal-contact-update-legend-financial-entity-valid')
    legendValidInvalidRestart('modal-contact-update-legend-iban-valid')
    legendValidInvalidRestart('modal-contact-update-legend-bban-valid')
    legendValidInvalidRestart('modal-contact-update-legend-bic-valid')
    legendValidInvalidRestart('modal-contact-update-legend-country-valid')
    legendValidInvalidRestart('modal-contact-update-legend-currency-valid')
    legendValidInvalidRestart('modal-contact-update-legend-language-valid')
    legendValidInvalidRestart('modal-contact-update-legend-qr-type-valid')

    if (contact && contact.data.uuid.length > 0) {
      if (contact.type === 'person') {
        legendValidIcon('modal-contact-update-input-first-name', 'modal-contact-update-legend-first-name-valid')
        legendValidIcon('modal-contact-update-input-last-name', 'modal-contact-update-legend-last-name-valid')
        legendValidIcon('modal-contact-update-input-email', 'modal-contact-update-legend-email-valid')
      }
      if (contact.type === 'company') {
        legendValidIcon('modal-contact-update-input-name', 'modal-contact-update-legend-name-valid')
      }
    }

    if (contacts && setContacts && setCompaniesOfContact && loadContactsForCompany) {
      setContact(null)
      setCompaniesOfContact(null)
    }

    setStarting(false)
  }

  useEffect( () => {
    if (contact && starting === true) {
      if (contact.type === 'company') {
        setLogo(uploadedUrl(lang, contact.data.logo))
        setCurrency({value: contact.data.currency.code, object: contact.data.currency, valid: true})
      }
      setCountry({value: contact.data.address.country, valid: true})
      setLanguage({value: contact.data.language, valid: true})
      setQrType({value: contact.data.qr_type, valid: true})
    } else {
      setLogo(removeUploadedFile())
      setCountry({value: '', valid: true})
      setCurrency({value: '', valid: true, object: null})
      setLanguage({value: '', valid: true})
      setQrType({value: '', valid: true})
    }

    setName({value: '', valid: true})
    setFirstName({value: '', valid: true})
    setLastName({value: '', valid: true})
    setEmail({value: '', valid: true})
    setPhone({value: '', valid: true})
    setWebsite({value: '', valid: true})
    setVatNumber({value: '', valid: true})
    setRegisterNumber({value: '', valid: true})
    setStreet({value: '', valid: true})
    setStreetNumber({value: '', valid: true})
    setAdditional({value: '', valid: true})
    setAdministrativeArea({value: '', valid: true})
    setCity({value: '', valid: true})
    setZipCode({value: '', valid: true})
    setFinancialEntity({value: '', valid: true})
    setIban({value: '', valid: true})
    setBban({value: '', valid: true})
    setBic({value: '', valid: true})

    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, starting])

  return (
    <>
      <div id="modal-contact-update" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="text-capitalize">{lang.labels.updateContactData}</h2>
            </div>
            <div className="modal-body p-0">
              { contact && starting === true
                ?
                <div className="form">
                  <div id="modal-contact-update-container" className="scroll-y d-flex flex-column">
                    <Accordion defaultActiveKey="0" flush>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header bsPrefix='border'>
                          <span className="fw-boldest fs-5">{lang.labels.contactIdentity}</span>
                        </Accordion.Header>
                        <Accordion.Body>
                          { contact.type === "person" &&
                            <>
                              <div className="fv-row mb-3">
                                <label className="form-label">{lang.labels.firstName}</label>
                                <input id="modal-contact-update-input-first-name" className={`form-control form-control-solid ${contact.type === "person" && contact.data.uuid.length > 0 && "is-valid"}`} type="text" autoComplete="off" name="first-name" disabled={contact.type === "person" && contact.data.uuid.length > 0} placeholder={contact.data.first_name} value={firstName.value} onChange={handleChangeFirstName} onKeyUp={validateFirstName} onBlur={validateFirstName} />
                                <AppLegend component={"modal-contact-update"} attribute={{check: firstName.valid, name: "first-name", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                              </div>
                              <div className="fv-row mb-3">
                                <label className="form-label">{lang.labels.lastName}</label>
                                <input id="modal-contact-update-input-last-name" className={`form-control form-control-solid ${contact.type === "person" && contact.data.uuid.length > 0 && "is-valid"}`} type="text" autoComplete="off" name="last-name" disabled={contact.type === "person" && contact.data.uuid.length > 0} placeholder={contact.data.last_name} value={lastName.value} onChange={handleChangeLastName} onKeyUp={validateLastName} onBlur={validateLastName} />
                                <AppLegend component={"modal-contact-update"} attribute={{check: lastName.valid, name: "last-name", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                              </div>
                            </>
                          }
                          { contact.type === "company" &&
                            <>
                              <div className="fv-row mb-3">
                                <label className="form-label">{lang.labels.companyName}</label>
                                <input id="modal-contact-update-input-name" className={`form-control form-control-solid ${contact.type === "company" && contact.data.uuid.length > 0 && "is-valid"}`} type="text" autoComplete="off" name="name" disabled={contact.type === "company" && contact.data.uuid.length > 0} placeholder={contact.data.name} value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                                <AppLegend component={"modal-contact-update"} attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                              </div>
                              <div className="fv-row mb-7">
                                <label className="form-label">
                                  <div className="d-flex align-items-center">
                                    {lang.labels.companyLogo}
                                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title= {lang.labels.theFileMustBeFormat}><FaExclamationCircle /></i>
                                  </div>
                                </label>
                                <div className="mt-5">
                                  <AppInputImage image={logo} setImage={setLogo}></AppInputImage>
                                </div>
                              </div>
                            </>
                          }
                          <div className="fv-row mb-3">
                            <label className="form-label">{lang.labels.email}</label>
                            <input id="modal-contact-update-input-email" className={`form-control form-control-solid ${contact.type === "person" && contact.data.uuid.length > 0  && "is-valid"}`} type="email" autoComplete="off" name="email" disabled={contact.type === "person" && contact.data.uuid.length > 0} placeholder={contact.data.email} value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
                            <AppLegend component={"modal-contact-update"} attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">{lang.labels.phoneNumber}</label>
                            <input id="modal-contact-update-input-phone" className="form-control form-control-solid" type="tel" autoComplete="off" name="phone" placeholder={contact.data.phone} value={phone.value} onChange={handleChangePhone} onKeyUp={validatePhone} onBlur={validatePhone} />
                            <AppLegend component={"modal-contact-update"} attribute={{check: phone.valid, name: "phone", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">{lang.labels.webSite}</label>
                            <input id="modal-contact-update-input-website" className="form-control form-control-solid" type="url" autoComplete="off" name="website" placeholder={contact.data.website} value={website.value} onChange={handleChangeWebsite} onKeyUp={validateWebsite} onBlur={validateWebsite} />
                            <AppLegend component={"modal-contact-update"} attribute={{check: website.valid, name: "website", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          { contact.type === "company" &&
                            <>
                              <div className="fv-row mb-3 d-none">
                                <label className="form-label">{lang.labels.registerNumber}</label>
                                <input id="modal-contact-update-input-register-number" className="form-control form-control-solid" type="text" autoComplete="off" name="register-number" placeholder={contact.data.register_number} value={registerNumber.value} onChange={handleChangeRegisterNumber} onKeyUp={validateRegisterNumber} onBlur={validateRegisterNumber} />
                                <AppLegend component={"modal-contact-update"} attribute={{check: registerNumber.valid, name: "register-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                              </div>
                              <div className="fv-row mb-3">
                                <label className="form-label">
                                  <div className="d-flex align-items-center">
                                    {lang.labels.vatNumber}
                                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.identificationNumberForACompany}><FaExclamationCircle /></i>
                                  </div>
                                </label>
                                <input id="modal-contact-update-input-vat-number" className="form-control form-control-solid"  type="text" autoComplete="off" name="vat-number" placeholder={contact.data.vat_number} value={vatNumber.value} onChange={handleChangeVatNumber} onKeyUp={validateVatNumber} onBlur={validateVatNumber} />
                                <AppLegend component={"modal-contact-update"} attribute={{check: vatNumber.valid, name: "vat-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                              </div>
                            </>
                          }
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="1">
                        <Accordion.Header bsPrefix='border'>
                          <span className="fw-boldest fs-5">{lang.labels.contactInformation}</span>
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="row mx-0">
                            <div className="col-md-8 fv-row mb-3 px-0 pe-md-2">
                              <label className="form-label">{lang.labels.street}</label>
                              <input id="modal-contact-update-input-street" className="form-control form-control-solid" type="text" autoComplete="off" name="street" placeholder={contact.data.address.street} value={street.value} onChange={handleChangeStreet} onKeyUp={validateStreet} onBlur={validateStreet} />
                              <AppLegend component={"modal-contact-update"} attribute={{check: street.valid, name: "street", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                            </div>
                            <div className="col-md-4 fv-row mb-3 px-2 ps-md-0">
                              <label className="form-label">
                                <div className="d-flex align-items-center">
                                  {lang.labels.number}
                                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifAddressDoesNotHaveANumber}><FaExclamationCircle /></i>
                                </div>
                              </label>
                              <input id="modal-contact-update-input-street-number" className="form-control form-control-solid" type="text" autoComplete="off" name="number" placeholder={contact.data.address.number} value={streetNumber.value} onChange={handleChangeStreetNumber} onKeyUp={validateStreetNumber} onBlur={validateStreetNumber} />
                              <AppLegend component={"modal-contact-update"} attribute={{check: streetNumber.valid, name: "street-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                            </div>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                {lang.labels.additional}
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifNecessaryEnterSomethingReferentialRegardingAddress}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <input id="modal-contact-update-input-additional" className="form-control form-control-solid" type="text" autoComplete="off" name="additional" placeholder={contact.data.address.additional} value={additional.value} onChange={handleChangeAdditional} onKeyUp={validateAdditional} onBlur={validateAdditional} />
                            <AppLegend component={"modal-contact-update"} attribute={{check: additional.valid, name: "additional", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">{lang.labels.administrativeArea}</label>
                            <input id="modal-contact-update-input-administrative-area" className="form-control form-control-solid" type="text" autoComplete="off" name="administrative-area" placeholder={contact.data.address.administrative_area} value={administrativeArea.value} onChange={handleChangeAdministrativeArea} onKeyUp={validateAdministrativeArea} onBlur={validateAdministrativeArea} />
                            <AppLegend component={"modal-contact-update"} attribute={{check: administrativeArea.valid, name: "administrative-area", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="row mx-0">
                            <div className="col-md-4 fv-row mb-3 px-0 pe-md-2">
                              <label className="form-label">
                                <div className="d-flex align-items-center">
                                  {lang.labels.zipCode}
                                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterThePostalCodeOfTheCity}><FaExclamationCircle /></i>
                                </div>
                              </label>
                              <input id="modal-contact-update-input-zip-code" className="form-control form-control-solid" type="text" autoComplete="off" name="zip-code" placeholder={contact.data.address.zip_code} value={zipCode.value} onChange={handleChangeZipCode} onKeyUp={validateZipCode} onBlur={validateZipCode} />
                              <AppLegend component={"modal-contact-update"} attribute={{check: zipCode.valid, name: "zip-code", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                            </div>
                            <div className="col-md-8 fv-row mb-3 px-0 ps-md-2">
                              <label className="form-label">{lang.labels.city}</label>
                              <input id="modal-contact-update-input-city" className="form-control form-control-solid" type="text" autoComplete="off" name="city" placeholder={contact.data.address.city} value={city.value} onChange={handleChangeCity} onKeyUp={validateCity} onBlur={validateCity} />
                              <AppLegend component={"modal-contact-update"} attribute={{check: city.valid, name: "city", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                            </div>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">{lang.labels.country}</label>
                            <AppInputCountry component={"modal-contact-update"} options={{small: false, disabled: false, placeholder: null}} country={country} setCountry={setCountry}></AppInputCountry>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="2">
                        <Accordion.Header bsPrefix='border'>
                          <span className="fw-boldest fs-5">{lang.labels.legalInformation}</span>
                        </Accordion.Header>
                        <Accordion.Body>
                          { contact.type === "company" &&
                            <div className="fv-row mb-3">
                              <label className="form-label">{lang.labels.currency}</label>
                              <AppInputCurrency component={"modal-contact-update"} currency={currency} setCurrency={setCurrency}></AppInputCurrency>
                            </div>
                          }
                          <div className="fv-row mb-3">
                            <label className="form-label">{lang.labels.financialEntity}</label>
                            <div className="position-relative">
                              <input className="form-control form-control-solid pe-13" type="text" autoComplete="off" name="account-name" placeholder={contact.data.bank_account.financial_entity} value={financialEntity.value} onChange={handleChangeAccountName} onKeyUp={validateFinancialEntity} onBlur={validateFinancialEntity} />
                              <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                                <img src={iconBank} alt={lang.labels.bank} className="h-20px" />
                              </div>
                            </div>
                            <AppLegend component={"modal-contact-update"} attribute={{check: financialEntity.valid, name: "financial-entity", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                {lang.labels.iban}
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumber}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <div className="position-relative">
                              <input id="modal-contact-update-input-iban" className="form-control form-control-solid pe-13" type="text" autoComplete="off" name="iban" placeholder={ibanFormat(contact.data.bank_account.iban)} value={iban.value} onChange={handleChangeIban} onKeyUp={validateIban} onBlur={validateIban} />
                              <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                                <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                              </div>  
                            </div>
                            <AppLegend component={"modal-contact-update"} attribute={{check: iban.valid, name: "iban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                {lang.labels.bban}
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.basicBankAccountNumber}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <div className="position-relative">
                              <input id="modal-contact-update-input-bban" className="form-control form-control-solid pe-13" type="text" autoComplete="off" name="bban" placeholder={contact.data.bank_account.bban} value={bban.value} onChange={handleChangeBban} onKeyUp={validateBban} onBlur={validateBban} />
                              <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                                <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                              </div>
                            </div>
                            <AppLegend component={"modal-contact-update"} attribute={{check: bban.valid, name: "bban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                {lang.labels.bicSwift}
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.bankIdentifierCode}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <div className="position-relative">
                              <input id="modal-contact-update-input-bic" className="form-control form-control-solid pe-13" type="text" autoComplete="off" name="bic" placeholder={bicFormat(contact.data.bank_account.bic)} value={bic.value} onChange={handleChangeBic} onKeyUp={validateBic} onBlur={validateBic} />
                              <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                                <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                              </div>
                            </div>
                            <AppLegend component={"modal-contact-update"} attribute={{check: bic.valid, name: "bic", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="3">
                        <Accordion.Header bsPrefix='border'>
                          <span className="fw-boldest fs-5">{lang.labels.fileDownloadSettings}</span>
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                {lang.labels.language}
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.languageToDownload}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <AppInputLanguage component={"modal-contact-update"} language={language} setLanguage={setLanguage}></AppInputLanguage>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                <span>{lang.labels.qrType}</span>
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.shapesOrVersionsOfQR}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <AppInputQrType component={"modal-contact-update"} qrType={qrType} setQrType={setQrType}></AppInputQrType>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                </div>
                :
                <AppLoading></AppLoading>
              }
            </div>
            <div className="modal-footer flex-center">
              <button className="btn btn-light mx-2" type="reset" onClick={executeHideModalContactUpdate}>{lang.labels.discard}</button>
              { companiesOfContact && companiesOfContact.length === 1
                ?
                <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitContactUpdate}>
                  <span className="indicator-label">{lang.labels.update}</span>
                  <span className="indicator-progress">
                    {lang.labels.pleaseWait}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
                :
                <button className="btn btn-primary mx-2" type="button" onClick={executeShowModalContactUpdateExtend}>{lang.labels.extendChanges}</button>
              }
            </div>
          </div>
        </div>
      </div>
      <AppModalContactUpdateExtend companyForUser={companyForUser} contact={contact} contacts={contacts} companiesOfContact={companiesOfContact} values={values} setContact={setContact} setContacts={setContacts} loadContactsForCompany={loadContactsForCompany} restartModalMain={restartModal}></AppModalContactUpdateExtend>
    </>
  )
};

export default AppModalContactUpdate;
